import { useEffect, useMemo, useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, usePermissions, useTranslate } from "@refinedev/core";
import {
  Button,
  PencilIcon,
  PlusCircleSolidIcon,
  TrashIcon,
  ConfirmDialog,
  Modal,
  type Column,
  type SortState,
} from "@fawkes/ui";

import { formatDateTime } from "../../i18n/format";
import {
  statusLabel,
  originLabel,
  typeLabel,
  categoryLabel,
  statusBadgeColors,
  originBadgeColors,
} from "./commLabels";
import { CommBadge } from "./CommBadge";
import { IndeelbareTabel } from "../../components/IndeelbareTabel";

// communicatie (read-foundation UI): the templates list — GET /api/communicatie/templates with the
// server-side type/category/status/q filters. The endpoint returns a bare JSON array (no paging this
// slice). The url is RELATIVE (dataProvider prepends /api once). Row → read-only detail via onOpen.

export interface CommTemplateSummary {
  id: string;
  templateKey: string;
  name: string;
  description: string | null;
  type: string | null;
  category: string | null;
  status: string | null;
  origin: string | null;
  languageCode: string | null;
  vestigingId: number | null;
  publicationDate: string | null;
  modifiedDate: string | null;
  modifiedByUser: string | null;
}

export function CommTemplateList({ onOpen }: { onOpen?: (id: string) => void } = {}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  const { data: permissions } = usePermissions<string[]>();
  const canEdit = Array.isArray(permissions) && permissions.includes("ROLE_COMMUNICATIE_EDITOR");
  const { mutate: create, isLoading: creating } = useCustomMutation();
  const [nieuwOpen, setNieuwOpen] = useState(false);
  // Het sjabloon dat op het punt staat verwijderd te worden. Verwijderen kon in dit scherm niet:
  // de server heeft DELETE, de lijst had er geen knop voor — dus een sjabloon dat je niet meer
  // gebruikt bleef staan.
  const [verwijderRij, setVerwijderRij] = useState<CommTemplateSummary | null>(null);
  // Eén rij tegelijk gekozen; de knoppen erboven werken daarop. Knoppen ín elke rij maakten van de
  // laatste kolom een kolom vol knoppen, en bij twintig sjablonen leest dat niet meer.
  const [gekozen, setGekozen] = useState<string | null>(null);
  const { mutate: verwijder, isLoading: deleting } = useCustomMutation();
  const [nieuwFout, setNieuwFout] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [type, setType] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(handle);
  }, [query]);

  const url = useMemo(() => {
    const params = new URLSearchParams();
    if (type) params.set("type", type);
    if (category) params.set("category", category);
    if (status) params.set("status", status);
    if (debounced) params.set("q", debounced);
    const qs = params.toString();
    return qs ? `communicatie/templates?${qs}` : "communicatie/templates";
  }, [type, category, status, debounced]);

  const { data, isLoading, isError, refetch } = useCustom<CommTemplateSummary[]>({ url, method: "get" });
  const geladen = data?.data ?? [];

  // Sorteren op elke kolom, zoals op acc. Met veertien sjablonen die allemaal "Afdruk / Asiel /
  // Actief" zijn is de naam het enige waarop je ze uit elkaar houdt; dan wil je ze op datum of op
  // categorie kunnen zetten. De tabel sorteert zelf niet — dat doen we hier, over de rijen die we
  // toch al binnen hebben, dus zonder de server opnieuw te bevragen.
  const [sort, setSort] = useState<SortState | undefined>(undefined);
  const rows = useMemo(() => {
    if (!sort) return geladen;
    const richting = sort.dir === "desc" ? -1 : 1;
    return [...geladen].sort((a, b) => {
      const links = (a as unknown as Record<string, unknown>)[sort.key];
      const rechts = (b as unknown as Record<string, unknown>)[sort.key];
      // Leeg zakt altijd naar onderen, ongeacht de richting: een lege cel is geen waarde.
      if (links == null || links === "") return rechts == null || rechts === "" ? 0 : 1;
      if (rechts == null || rechts === "") return -1;
      return String(links).localeCompare(String(rechts), "nl", { numeric: true }) * richting;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geladen.map((r) => r.id).join(","), sort?.key, sort?.dir]);

  // Category options are derived from the loaded rows — the vocabulary is data-driven (ASIEL/DIER/…),
  // so we don't hard-code a list that could drift from the store.
  const categoryOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.category).filter((c): c is string => !!c))).sort(),
    [geladen],
  );

  const columns: Column<CommTemplateSummary>[] = [
    { header: t("communicatieRead.template.column.name"), sortKey: "name", cell: (r) => r.name },
    {
      header: t("communicatieRead.template.column.type"),
      sortKey: "type",
      cell: (r) => typeLabel(r.type, t),
    },
    {
      header: t("communicatieRead.template.column.category"),
      sortKey: "category",
      cell: (r) => categoryLabel(r.category, t),
    },
    {
      header: t("communicatieRead.template.column.status"),
      sortKey: "status",
      cell: (r) =>
        r.status ? (
          <CommBadge colors={statusBadgeColors(r.status)}>{statusLabel(r.status, t)}</CommBadge>
        ) : (
          "—"
        ),
    },
    {
      header: t("communicatieRead.template.column.origin"),
      sortKey: "origin",
      cell: (r) =>
        r.origin ? (
          <CommBadge colors={originBadgeColors(r.origin)}>{originLabel(r.origin, t)}</CommBadge>
        ) : (
          "—"
        ),
    },
    {
      header: t("communicatieRead.template.column.modified"),
      sortKey: "modifiedDate",
      cell: (r) => formatDateTime(r.modifiedDate, locale),
    },
    {
      header: t("communicatieRead.template.column.modifiedBy"),
      sortKey: "modifiedByUser",
      cell: (r) => r.modifiedByUser ?? "—",
    },
  ];


  return (
    <div className="comm-list">
      <div className="comm-list-filters">
        <div className="comm-list-filter">
          <label htmlFor="comm-tpl-zoek">{t("communicatieRead.template.filter.search")}</label>
          <input
            id="comm-tpl-zoek"
            type="search"
            autoComplete="off"
            className="fui-field__input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="comm-list-filter">
          <label htmlFor="comm-tpl-type">{t("communicatieRead.template.filter.type")}</label>
          <select
            id="comm-tpl-type"
            className="fui-field__input"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            <option value="">{t("communicatieRead.filter.all")}</option>
            <option value="EMAIL">{t("communicatieRead.type.EMAIL")}</option>
            <option value="PRINT">{t("communicatieRead.type.PRINT")}</option>
          </select>
        </div>
        <div className="comm-list-filter">
          <label htmlFor="comm-tpl-category">{t("communicatieRead.template.filter.category")}</label>
          <select
            id="comm-tpl-category"
            className="fui-field__input"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">{t("communicatieRead.filter.all")}</option>
            {categoryOptions.map((c) => (
              <option key={c} value={c}>
                {categoryLabel(c, t)}
              </option>
            ))}
          </select>
        </div>
        <div className="comm-list-filter">
          <label htmlFor="comm-tpl-status">{t("communicatieRead.template.filter.status")}</label>
          <select
            id="comm-tpl-status"
            className="fui-field__input"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">{t("communicatieRead.filter.all")}</option>
            <option value="CONCEPT">{t("communicatieRead.status.CONCEPT")}</option>
            <option value="ACTIEF">{t("communicatieRead.status.ACTIEF")}</option>
            <option value="INACTIEF">{t("communicatieRead.status.INACTIEF")}</option>
            <option value="FOUT">{t("communicatieRead.status.FOUT")}</option>
          </select>
        </div>
      </div>

      {/* Aanmaken, openen en verwijderen staan bij elkaar boven de tabel en werken op de gekozen rij.
          Aanmaken en verwijderen konden hier helemaal niet: de server heeft de adressen, de lijst had
          er geen knoppen voor. */}
      <div className="comm-list-acties">
        {canEdit && (
          <Button onClick={() => setNieuwOpen(true)}>
            <PlusCircleSolidIcon width={16} height={16} /> {t("communicatieRead.template.new")}
          </Button>
        )}
        <Button
          variant="secondary"
          disabled={!gekozen}
          onClick={() => gekozen && onOpen?.(gekozen)}
        >
          <PencilIcon width={15} height={15} /> {t("communicatieRead.template.edit")}
        </Button>
        {canEdit && (
          <Button
            variant="secondary"
            disabled={!gekozen}
            onClick={() => {
              const rij = rows.find((r) => r.id === gekozen);
              if (rij) setVerwijderRij(rij);
            }}
          >
            <TrashIcon width={15} height={15} /> {t("common.delete")}
          </Button>
        )}
      </div>

      {isLoading ? (
        <p className="fawkes-muted">{t("common.loading")}</p>
      ) : isError ? (
        <p className="fawkes-error">{t("communicatieRead.loadFailed")}</p>
      ) : (
        <div className="comm-list-grid">
        <IndeelbareTabel tabelKey="communicatie-comm-template-list"
          columns={columns}
          rows={rows}
          getRowKey={(r) => r.id}
          selectedRowKey={gekozen ?? undefined}
          onRowClick={(r) => setGekozen(r.id)}
          onRowDoubleClick={(r) => onOpen?.(r.id)}
          sort={sort}
          onSortChange={setSort}
          empty={t("communicatieRead.template.empty")}
        />
        </div>
      )}

      <ConfirmDialog
        open={verwijderRij != null}
        title={t("communicatieRead.template.delete.title")}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={() => {
          if (!verwijderRij || deleting) return;
          verwijder(
            { url: `communicatie/templates/${verwijderRij.id}`, method: "delete", values: {} },
            {
              onSuccess: () => { setVerwijderRij(null); refetch(); },
              onError: () => setVerwijderRij(null),
            },
          );
        }}
        onCancel={() => setVerwijderRij(null)}
      >
        {t("communicatieRead.template.delete.confirm")}
        {verwijderRij ? ` (${verwijderRij.name})` : ""}
      </ConfirmDialog>

      {nieuwOpen && (
        <NieuwSjabloonDialog
          bezig={creating}
          fout={nieuwFout}
          categorieën={categoryOptions}
          onSluiten={() => { setNieuwOpen(false); setNieuwFout(null); }}
          onBewaar={(waarden) => {
            setNieuwFout(null);
            create(
              { url: "communicatie/templates", method: "post", values: waarden },
              {
                onSuccess: (res) => {
                  setNieuwOpen(false);
                  refetch();
                  // Meteen door naar de editor: je maakt een sjabloon aan om er iets in te zetten.
                  const id = (res?.data as { id?: string } | undefined)?.id;
                  if (id && onOpen) onOpen(id);
                },
                onError: () => setNieuwFout(t("communicatieRead.template.new.failed")),
              },
            );
          }}
          t={t}
        />
      )}
    </div>
  );
}

/**
 * Een nieuw sjabloon: alleen wat de server verplicht stelt, de rest doe je in de editor.
 *
 * <p>Naam, type en categorie zijn genoeg om er een te maken; de inhoud, het onderwerp en de
 * instellingen komen daarna. Zo hoef je niet eerst een formulier van vijftien velden door voordat je
 * kunt beginnen — en dat is ook waarom hij na het bewaren meteen doorschiet naar de editor.
 */
function NieuwSjabloonDialog({
  bezig,
  fout,
  categorieën,
  onBewaar,
  onSluiten,
  t,
}: {
  bezig: boolean;
  fout: string | null;
  categorieën: string[];
  onBewaar: (waarden: { name: string; type: string; category: string; languageCode: string }) => void;
  onSluiten: () => void;
  t: (k: string) => string;
}) {
  const [naam, setNaam] = useState("");
  const [type, setType] = useState("PRINT");
  const [categorie, setCategorie] = useState(categorieën[0] ?? "ASIEL");
  const schoon = naam.trim();

  return (
    <Modal
      open
      title={t("communicatieRead.template.new")}
      onClose={onSluiten}
      footer={
        <>
          <Button variant="secondary" onClick={onSluiten} disabled={bezig}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => onBewaar({ name: schoon, type, category: categorie, languageCode: "nl" })}
            disabled={bezig || schoon.length === 0}
          >
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="fui-field">
        <label className="fui-field__label" htmlFor="comm-nieuw-naam">
          {t("communicatieRead.template.column.name")}
        </label>
        <input
          id="comm-nieuw-naam"
          className="fui-field__input"
          value={naam}
          onChange={(e) => setNaam(e.target.value)}
          maxLength={250}
          autoFocus
        />
      </div>
      <div className="fui-field">
        <label className="fui-field__label" htmlFor="comm-nieuw-type">
          {t("communicatieRead.template.column.type")}
        </label>
        <select
          id="comm-nieuw-type"
          className="fui-field__input"
          value={type}
          onChange={(e) => setType(e.target.value)}
        >
          <option value="PRINT">{t("communicatieRead.type.PRINT")}</option>
          <option value="EMAIL">{t("communicatieRead.type.EMAIL")}</option>
        </select>
      </div>
      <div className="fui-field">
        <label className="fui-field__label" htmlFor="comm-nieuw-categorie">
          {t("communicatieRead.template.column.category")}
        </label>
        <select
          id="comm-nieuw-categorie"
          className="fui-field__input"
          value={categorie}
          onChange={(e) => setCategorie(e.target.value)}
        >
          {(categorieën.length ? categorieën : ["ASIEL"]).map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>
      {fout ? <p className="fawkes-error">{fout}</p> : null}
    </Modal>
  );
}